import { createRef, useContext, useEffect, useRef, useState } from 'react'
import { Box, Icon, IconButton, LinearProgress, Typography } from '@mui/material'
import axios from 'axios'
import { Document, Page, pdfjs } from 'react-pdf'
import Bugsnag from '@bugsnag/js'

import { authContext } from 'contexts/AuthContext'
import { presignURL } from 'services/api/presign-url'
import { getFileExtension } from 'services/helper/preSignHelper'

import AdxInfoModal from '../modal/AdxInfoModal'
import AdxDropzone from '../AdxDropzone'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import AdxButton from 'components/custom/AdxButton'

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const AdxDropzoneImagePdf = ({
  // accept fileUrl from API
  file,
  label,
  title,
  subtitle,
  folder,
  disabled,
  setFieldValue,
  disabledPdf,
  disabledImage,
  errorMessage,
  isErrorShown,
  parentsDependencies = [],
  onChange = () => null,
  onReplace = () => null,

  // custom file accept
  customFileType,
  customDropzoneFileAccept,
}) => {
  // PENDING, UPLOADING, READY, ERROR
  const [uploadState, setUploadState] = useState('PENDING')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [fileUpload, setFileUpload] = useState([])
  const [errorFiles, setErrorFiles] = useState()
  const [fileType, setFileType] = useState([])
  const [fileAccept, setFileAccept] = useState('')
  const dropzoneRef = createRef()

  const { auth } = useContext(authContext)

  const [errorModal, setErrorModal] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  const handleUpload = async (item) => {
    setUploadState('UPLOADING')

    // start uploading
    try {
      if (auth.data.tenant_id === undefined) {
        throw new Error('Internal error. Tenant id is undefined.')
      }

      setUploadState('UPLOADING')

      // S3 API
      const results = await presignURL({
        bucket: process.env.REACT_APP_AWS_BUCKET,
        key: `tenant_${auth.data.tenant_id}/${folder}/` + item?.name,
      })

      const awsRequest = axios.create()
      delete awsRequest.defaults.headers.common['Authorization']
      await awsRequest.put(results.data.url, item, {
        headers: {
          'Content-Type': item?.type,
        },
        onUploadProgress: (progressEvent) => {
          let percentComplete = progressEvent.loaded / progressEvent.total
          percentComplete = parseInt(percentComplete * 100)
          setUploadProgress(percentComplete)
        },
      })
      Object.defineProperty(item, 'awsUrl', {
        writable: true,
        value: results?.data?.url?.split('?')[0],
      })

      setFieldValue('file', item)
      setFileUpload(item)
      setUploadState('READY')
    } catch (e) {
      setErrorMsg(e.message)
      setErrorModal(true)
      setUploadState('ERROR')
      setErrorFiles([{ errors: [{ message: e }] }])
      Bugsnag.notify(e)
    }
  }
  const handleCancelUpload = () => {
    setUploadState('PENDING')
    setUploadProgress(0)
    setFileUpload({})
    setErrorFiles([])
    setFieldValue('file', '')
  }

  // responsive width pdf viewer
  const [initialWidth, setInitialWidth] = useState(null)
  const pdfWrapper = useRef(null)

  const setPdfSize = () => {
    if (pdfWrapper && pdfWrapper.current) {
      setInitialWidth(pdfWrapper.current.getBoundingClientRect().width)
    }
  }

  useEffect(() => {
    onChange(uploadState)
    window.addEventListener('load', setPdfSize())
    window.addEventListener('resize', setPdfSize())
    return () => {
      window.removeEventListener('resize', setPdfSize())
    }
  }, [uploadState, onChange])

  useEffect(() => {
    if (isErrorShown) {
      setUploadState('ERROR')
    }
  }, [isErrorShown])

  useEffect(() => {
    if (file) {
      setUploadState('READY')
    }

    if (!file) {
      setUploadState('PENDING')
    }

    if (disabledPdf) {
      setFileType(['photo'])
      setFileAccept('image/jpeg, image/png,')
    } else if (disabledImage) {
      setFileType(['document'])
      setFileAccept('application/pdf')
    } else if (customFileType) {
      setFileType(customFileType)
      setFileAccept(customDropzoneFileAccept)
    } else {
      setFileType(['photo', 'document'])
      setFileAccept('image/jpeg, image/png, application/pdf')
    }
  }, [...parentsDependencies, file]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box display="flex" flexDirection="column" rowGap={1}>
      <Typography variant="body1" color={disabled ? 'gray-inactive-button.main' : 'dark-text.main'}>
        {label}
      </Typography>
      <Box height={450}>
        {/******************/}
        {/*     PENDING    */}
        {/******************/}
        {uploadState === 'PENDING' && (
          <AdxDropzone
            sx={{ minHeight: 400 }}
            mainText={title}
            optionalText={subtitle}
            handleFiles={async (item) => {
              await handleUpload(item)
            }}
            fileType={fileType}
            setUploadState={setUploadState}
            useDropzoneObject={{
              accept: fileAccept,
              disabled,
            }}
            setErrFiles={setErrorFiles}
          />
        )}

        {/******************/}
        {/*    UPLOADING   */}
        {/******************/}
        {uploadState === 'UPLOADING' && (
          <Box
            width={1}
            minHeight={450}
            position="relative"
            borderRadius="4.5px"
            overflow="hidden"
            bgcolor="gray-background.main"
            sx={{ border: '3px solid', borderColor: 'gray-line.main' }}
          >
            <Box></Box>

            <Box bgcolor="white.main" width={1} height={80} bottom={0} position="absolute">
              <LinearProgress
                variant="determinate"
                value={uploadProgress}
                color="alternate"
                sx={{
                  width: '100%',
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: 1,
                  width: '100%',
                  p: 2,
                }}
              >
                <Box display="flex" flexDirection="column" rowGap={1}></Box>
                <Box display="flex" alignItems="center" columnGap={1}>
                  <Typography
                    variant="body1"
                    color="light-gray-text.main"
                  >{`Uploading (${uploadProgress.toFixed(0)}%)`}</Typography>
                  <IconButton onClick={handleCancelUpload}>
                    <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/******************/}
        {/*      ERROR     */}
        {/******************/}
        {uploadState === 'ERROR' && (
          <Box display="flex" flexDirection="column" gap={2}>
            <AdxDropzone
              sx={{ minHeight: 400, borderColor: 'error.main' }}
              mainText={title}
              optionalText={subtitle}
              handleFiles={(item) => {
                setFileUpload(item)
                handleUpload(item)
              }}
              fileType={fileType}
              setUploadState={setUploadState}
              useDropzoneObject={{
                accept: fileAccept,
                disabled,
              }}
              setErrFiles={setErrorFiles}
            />
            <Box>
              {errorFiles?.map((item, index) => {
                if (index === 0) {
                  return item?.errors?.map((list) => {
                    switch (list?.code) {
                      case 'file-too-large':
                        return <AdxErrorMessage children="File too big" />

                      case 'file-invalid-type':
                        return <AdxErrorMessage children="Invalid file type" />
                      case 'too-many-files':
                        return <AdxErrorMessage children="Too many files" />
                      default:
                        return <AdxErrorMessage children="An error occured. Try resubmit file" />
                    }
                  })
                }
                return null
              }) || errorMessage}
            </Box>
          </Box>
        )}

        {/******************/}
        {/*      READY     */}
        {/******************/}
        {uploadState === 'READY' && (
          <Box
            width={1}
            minHeight={450}
            position="relative"
            borderRadius="4.5px"
            overflow="hidden"
            bgcolor="gray-background.main"
            sx={{ border: '3px solid', borderColor: 'gray-line.main' }}
          >
            {fileUpload.type === 'application/pdf' ||
            (file && getFileExtension(file?.url) === 'pdf') ? (
              <Box
                display="flex"
                justifyContent="center"
                height={370}
                width={1}
                ref={pdfWrapper}
                sx={{ objectFit: 'cover', objectPosition: '0% 0%' }}
              >
                <Document file={fileUpload?.preview || file?.url}>
                  <Page pageNumber={1} width={initialWidth} />
                </Document>
              </Box>
            ) : (
              <Box
                as="img"
                height={370}
                width={1}
                src={fileUpload?.preview || file?.url}
                sx={{ objectFit: 'cover', objectPosition: '0% 0%' }}
              />
            )}

            <Box bgcolor="white.main" width={1} bottom={0} position="absolute">
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  height: 1,
                  width: '100%',
                  p: 2,
                }}
              >
                <Box display="flex" flexDirection="column" rowGap={1}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }} maxWidth={350}>
                    {fileUpload?.name || file?.name}
                  </Typography>
                  {fileUpload?.size && (
                    <Typography variant="body2">
                      {(fileUpload?.size / 1000000).toFixed(1)} MB
                    </Typography>
                  )}
                </Box>
                <Box display="flex" alignItems="center" columnGap={1}>
                  <AdxButton
                    variant={'ghost'}
                    size={'small'}
                    onClick={() => window.open(fileUpload?.preview || file?.url)}
                  >
                    View
                  </AdxButton>
                  <AdxButton
                    variant={'ghost'}
                    size={'small'}
                    onClick={() => {
                      onReplace()
                      dropzoneRef.current.open()
                    }}
                  >
                    Replace
                  </AdxButton>
                  <IconButton onClick={handleCancelUpload}>
                    <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                  </IconButton>
                  <AdxDropzone
                    ref={dropzoneRef}
                    sx={{ display: 'none' }}
                    mainText={null}
                    optionalText={null}
                    handleFiles={(item) => {
                      setFileUpload(item)
                      handleUpload(item)
                    }}
                    fileType={fileType}
                    setUploadState={setUploadState}
                    useDropzoneObject={{
                      accept: fileAccept,
                      disabled,
                    }}
                    setErrFiles={setErrorFiles}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <AdxInfoModal
        variant="error"
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        muiIcon="error"
        iconColor="error.main"
        title="Error"
        subtitle={errorMsg || 'Error occured while uploading file'}
        footerButtons
        firstBtnLabel="Okay"
        firstBtnHandle={() => setErrorModal(false)}
      ></AdxInfoModal>
    </Box>
  )
}

export default AdxDropzoneImagePdf
